import React from 'react';

/*
 * One glass-sphere recipe, shared by every body on the site.
 *
 * Four layers make a sphere read as glass rather than as a filled circle:
 *   1. atmosphere -- a blurred halo of the body's own colour
 *   2. volume     -- highlight at the top, limb darkening at the edge
 *   3. bounce     -- light returning onto the lower limb; the real glass tell
 *   4. specular   -- a tight hotspot, plus a Fresnel rim that catches light
 *                    at grazing angles and is brightest along the top edge
 *
 * The light sits directly overhead, matching the elevation model, so the
 * hotspot is centred horizontally rather than thrown to one side.
 *
 * Colour-bearing layers take `fill` from the caller; the neutral overlays are
 * shared gradients in objectBoundingBox units, so one definition scales to any
 * radius.
 */

export const SphereDefs = ({ uid }) => (
  <>
    {/* Volume: lit crown, darkened limb. */}
    <radialGradient id={`sph-body-${uid}`} cx="50%" cy="30%" r="74%">
      <stop offset="0%" stopColor="#fff" style={{ stopOpacity: 'var(--tmc-sphere-crown)' }} />
      <stop offset="38%" stopColor="#fff" stopOpacity="0" />
      <stop offset="74%" stopColor="#000" style={{ stopOpacity: 'var(--tmc-sphere-limb-mid)' }} />
      <stop offset="100%" stopColor="#000" style={{ stopOpacity: 'var(--tmc-sphere-limb)' }} />
    </radialGradient>

    {/* Bounce light on the lower limb. */}
    <radialGradient id={`sph-bounce-${uid}`} cx="50%" cy="90%" r="48%">
      <stop offset="0%" stopColor="#fff" style={{ stopOpacity: 'var(--tmc-sphere-bounce)' }} />
      <stop offset="100%" stopColor="#fff" stopOpacity="0" />
    </radialGradient>

    {/* Specular hotspot. */}
    <radialGradient id={`sph-spec-${uid}`} cx="50%" cy="50%" r="50%">
      <stop offset="0%" stopColor="#fff" stopOpacity=".85" />
      <stop offset="48%" stopColor="#fff" stopOpacity=".14" />
      <stop offset="100%" stopColor="#fff" stopOpacity="0" />
    </radialGradient>

    {/* Fresnel rim: hottest along the top edge, faint band returning below. */}
    <linearGradient id={`sph-rim-${uid}`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#fff" style={{ stopOpacity: 'var(--tmc-sphere-rim)' }} />
      <stop offset="42%" stopColor="#fff" stopOpacity=".08" />
      <stop offset="100%" stopColor="#fff" style={{ stopOpacity: 'var(--tmc-sphere-rim-low)' }} />
    </linearGradient>

    <filter id={`sph-glow-${uid}`} x="-70%" y="-70%" width="240%" height="240%">
      <feGaussianBlur stdDeviation="5" />
    </filter>

    {/* Softens strata edges so bands read as belts rather than stripes. */}
    <filter id={`sph-belt-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="2.6" />
    </filter>
  </>
);

/** The halo, drawn behind a body so neighbouring glows can overlap cleanly. */
export const SphereAtmosphere = ({ uid, cx, cy, r, color, dimmed = false }) => (
  <g pointerEvents="none">
    {/* Contact shadow, thrown by the same overhead light as everything else. */}
    <ellipse
      className={`sphere-cast${dimmed ? ' is-dimmed' : ''}`}
      cx={cx}
      cy={cy + r * 0.5}
      rx={r * 0.92}
      ry={r * 0.5}
      fill="#000"
      filter={`url(#sph-glow-${uid})`}
    />
    <circle
      className={`sphere-glow${dimmed ? ' is-dimmed' : ''}`}
      cx={cx}
      cy={cy}
      r={r * 1.28}
      fill={color}
      filter={`url(#sph-glow-${uid})`}
    />
  </g>
);

/**
 * Everything that sits over a body's own colour. Rendered separately so a
 * banded core can put its strata underneath the same glass treatment.
 */
export const SphereGlass = ({ uid, cx, cy, r }) => (
  <g pointerEvents="none">
    <circle cx={cx} cy={cy} r={r} fill={`url(#sph-body-${uid})`} />
    <circle cx={cx} cy={cy} r={r} fill={`url(#sph-bounce-${uid})`} />
    <ellipse
      cx={cx}
      cy={cy - r * 0.54}
      rx={r * 0.30}
      ry={r * 0.16}
      fill={`url(#sph-spec-${uid})`}
    />
    <circle
      cx={cx}
      cy={cy}
      r={r - Math.max(0.5, r * 0.04)}
      fill="none"
      stroke={`url(#sph-rim-${uid})`}
      strokeWidth={Math.max(0.8, r * 0.07)}
    />
  </g>
);

/** A complete glass body in one call. */
const Sphere = ({ uid, cx, cy, r, color, className = '' }) => (
  <g className={className}>
    <circle cx={cx} cy={cy} r={r} fill={color} />
    <SphereGlass uid={uid} cx={cx} cy={cy} r={r} />
  </g>
);

export default Sphere;
